import type { JsonProjectAdmin } from 'src/store/retainers/admin-panel/slice';
import type { JsonProject } from 'src/store/retainers/projects/types';
import type { JsonLoan } from 'src/store/loans/types';

interface SortableItem {
  name: string;
  id: number;
}

const SORT_HANDLERS = {
  name_asc: (a: SortableItem, b: SortableItem) => a.name?.localeCompare(b.name),
  name_desc: (a: SortableItem, b: SortableItem) => b.name?.localeCompare(a.name),
  created_asc: (a: SortableItem, b: SortableItem) => a.id - b.id,
  created_desc: (a: SortableItem, b: SortableItem) => b.id - a.id,
} as const;

type SortKey = keyof typeof SORT_HANDLERS;

/**
 * Sort key every sidebar falls back to. It must stay one of `SORT_HANDLERS`'
 * keys, which are also the values offered by `SkySidebarSortButton`'s Select.
 */
export const DEFAULT_SORT_KEY: SortKey = 'name_asc';

/** Narrows an arbitrary string to a sort key `filterAndSort` can actually apply. */
export const isSortKey = (key: string): key is SortKey => {
  return key in SORT_HANDLERS;
};

export const filterAndSort = <T extends SortableItem>(
  items: T[] | null,
  sortKey: string,
  searchValue = '',
) => {
  if (!items) return [];

  let filteredData = items;

  if (searchValue.length >= 2) {
    const parts = searchValue.toLowerCase().split(' ');
    filteredData = items.filter((item) => {
      const itemString = item.name.toLowerCase();
      return parts.every((token) => itemString.includes(token));
    });
  }

  const sortFn = isSortKey(sortKey) ? SORT_HANDLERS[sortKey] : () => 0;
  return filteredData.slice().sort(sortFn);
};

export const setDefaultName = <T extends JsonProjectAdmin | JsonProject | JsonLoan>(
  items: T[] | null,
) => {
  if (!items) return null;

  return items.map((item) => ({
    ...item,
    name: item?.name ?? 'N/A',
  }));
};
